import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { TableContext } from "../../context";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import {
  McTable,
  McButton,
  McPagination,
  McTabBar,
  McTab,
} from "@maersk-global/mds-react-wrapper";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McPopover } from "@maersk-global/mds-react-wrapper/components-core/mc-popover";
import { McCheckboxGroup } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox-group";
import { McCheckbox } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import "../../App.css";

import AnalyticsSection from "./AnalyticsSection";
import ApplicationFormFields from "./ApplicationFormFields";
import {
  CHANNEL_TAB_LABELS,
  RECORDS_PER_PAGE,
  getChannelTableColumns,
} from "./constants";
import {
  filterChannelData,
  transformChannelData,
  downloadCSV,
  formatDataForDownload,
} from "./utils";
import {
  fetchAppDetailsByAppId,
  fetchAppDetailsByAppName,
  fetchIncidentDetails,
  submitNewApplication,
  updateApplication,
  validateMandatoryFields,
  fetchStatisticsData,
} from "./apiHelpers";

import axios from "axios";

// Import modal components (we'll create these next)
import StatisticsModal from "./StatisticsModal";
import AppDetailsModal from "./AppDetailsModal";

/**
 * RedGreenChannelTracker component for displaying analytics and channel tracking data.
 * Shows platform and portfolio analytics at the top, followed by Red/Green/All channel tables.
 */
export default function RedGreenChannelTracker() {
  const {
    redGreenAnalytics,
    redGreenChannelData,
    getRedGreenAnalytics,
    getRedGreenChannelData,
    getRedGreenAppDetails,
    tokenFetched,
    graphData,
    userRole,
  } = useContext(TableContext);

  const [activeChannelTab, setActiveChannelTab] = useState("Red Channel");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("added_to_red_channel");
  const [sortDirection, setSortDirection] = useState("descending");

  // Column filters state
  const [platformColumnFilters, setPlatformColumnFilters] = useState({});
  const [portfolioColumnFilters, setPortfolioColumnFilters] = useState({});
  const [channelColumnFilters, setChannelColumnFilters] = useState({});

  // Add/Edit Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [originalAppName, setOriginalAppName] = useState("");
  const [originalIncidentId, setOriginalIncidentId] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    app_id: "",
    app_name: "",
    platform: "",
    portfolio: "",
    status: "Red",
    trigger_type: "",
    incident: "",
    priority: "",
    incident_dates: "",
    added_to_red_channel: DateTime.now(),
    moved_to_green_channel_on: null,
    first_review: DateTime.now().plus({ months: 2 }),
    final_review: DateTime.now().plus({ months: 3 }),
    application_representative: "",
    deployment_type: "",
    deployment_details: "",
    comments: "",
  });

  const [editFormData, setEditFormData] = useState({});
  const [appNameOptions, setAppNameOptions] = useState([]);
  const [editAppNameOptions, setEditAppNameOptions] = useState([]);
  const appNameTypeaheadRef = useRef(null);
  const editAppNameTypeaheadRef = useRef(null);

  // Track manually set dates
  const manuallySetDatesRef = useRef({
    first_review: false,
    final_review: false,
    moved_to_green_channel_on: false,
  });

  const editManuallySetDatesRef = useRef({
    first_review: false,
    final_review: false,
    moved_to_green_channel_on: false,
  });

  // Debounce timers
  const appIdTimerRef = useRef(null);
  const appNameTimerRef = useRef(null);
  const incidentTimerRef = useRef(null);
  const editAppIdTimerRef = useRef(null);
  const editAppNameTimerRef = useRef(null);
  const editIncidentTimerRef = useRef(null);

  // App Details Modal State
  const [isAppDetailsModalOpen, setIsAppDetailsModalOpen] = useState(false);
  const [appDetailsData, setAppDetailsData] = useState(null);
  const [appDetailsLoading, setAppDetailsLoading] = useState(false);

  // Statistics Modal State
  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);
  const [statisticsData, setStatisticsData] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [postmortemPreventiveMeasures, setPostmortemPreventiveMeasures] = useState([]);
  const [selectedRowForStats, setSelectedRowForStats] = useState(null);
  const [incidentCounts, setIncidentCounts] = useState(null);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const darkPrimaryTheme = createTheme({
    palette: {
      primary: {
        main: "#141414",
      },
    },
  });

  useEffect(() => {
    if (tokenFetched) {
      loadData();
    }
  }, [tokenFetched]);

  useEffect(() => {
    setCurrentPage(1);
    setChannelColumnFilters({}); // Reset channel filters when tab changes
  }, [activeChannelTab]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([getRedGreenAnalytics(), getRedGreenChannelData()]);
    setLoading(false);
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleChannelTabChange = (e) => {
    const tabIndex = e.detail;
    const newTabLabel = CHANNEL_TAB_LABELS[tabIndex];
    setActiveChannelTab(newTabLabel);
  };

  const getChannelTabIndex = () => {
    return CHANNEL_TAB_LABELS.indexOf(activeChannelTab);
  };

  // Download handlers
  const handleDownloadPlatformAnalytics = () => {
    const platformData = redGreenAnalytics?.platform_wise?.map((item) => ({
      Platform: item.platform || "N/A",
      "Total Apps": item.total_apps,
      "Red Channel": item.app_count_red_channel,
      "Green Channel": item.app_count_green_channel,
      "Red %": item.total_apps > 0
        ? Math.round((item.app_count_red_channel / item.total_apps) * 100)
        : 0,
    }));
    downloadCSV(platformData, `Platform_Analytics_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`);
  };

  const handleDownloadPortfolioAnalytics = () => {
    const portfolioData = redGreenAnalytics?.portfolio_wise?.map((item) => ({
      Portfolio: item.portfolio || "N/A",
      "Total Apps": item.total_apps,
      "Red Channel": item.app_count_red_channel,
      "Green Channel": item.app_count_green_channel,
      "Red %": item.total_apps > 0
        ? Math.round((item.app_count_red_channel / item.total_apps) * 100)
        : 0,
    }));
    downloadCSV(portfolioData, `Portfolio_Analytics_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`);
  };

  const handleDownloadClick = () => {
    const data = formatDataForDownload(filteredChannelData);
    downloadCSV(data, `RedGreen_Channel_${activeChannelTab}_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`);
  };

  // Statistics Modal handlers
  const handleOpenStatisticsModal = async (row) => {
    setSelectedRowForStats(row);
    setIsStatisticsModalOpen(true);
    setStatisticsLoading(true);
    try {
      const { statisticsData: stats, incidentCounts: counts, postmortemPreventiveMeasures: measures } = await fetchStatisticsData(
        row.app_id,
        row.app_name,
        row.incident_dates,
        row.incident
      );
      setStatisticsData(stats);
      setIncidentCounts(counts);
      setPostmortemPreventiveMeasures(measures || []);
    } catch (error) {
      setStatisticsData(null);
      setIncidentCounts(null);
      setPostmortemPreventiveMeasures([]);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const handleCloseStatisticsModal = () => {
    setIsStatisticsModalOpen(false);
    setSelectedRowForStats(null);
    setStatisticsData(null);
    setIncidentCounts(null);
    setPostmortemPreventiveMeasures([]);
  };

  // App Details Modal handlers
  const handleAppIdClick = async (appName, incident) => {
    setIsAppDetailsModalOpen(true);
    setAppDetailsLoading(true);
    try {
      const data = await getRedGreenAppDetails(appName, incident);
      const detailsData = Array.isArray(data) && data.length > 0 ? data[0] : data;
      setAppDetailsData(detailsData);
    } catch (error) {
      console.error("Error fetching app details:", error);
      setAppDetailsData(null);
    } finally {
      setAppDetailsLoading(false);
    }
  };

  const handleCloseAppDetailsModal = () => {
    setIsAppDetailsModalOpen(false);
    setAppDetailsData(null);
  };

  // Delete Modal handlers
  const handleOpenDeleteModal = (row) => {
    setRecordToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannel?app_name=${encodeURIComponent(recordToDelete.app_name)}&incident_id=${recordToDelete.incident}&user_email=${encodeURIComponent(graphData?.mail)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
          },
        }
      );
      
      toast.success("Record deleted successfully", {
        position: "top-center",
        autoClose: 3000,
      });
      
      handleCloseDeleteModal();
      loadData();
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error(error.response?.data?.message || "Failed to delete record", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // ADD MODAL HANDLERS
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    setFormData({
      app_id: "",
      app_name: "",
      platform: "",
      portfolio: "",
      status: "Red",
      trigger_type: "",
      incident: "",
      priority: "",
      incident_dates: "",
      added_to_red_channel: DateTime.now(),
      moved_to_green_channel_on: null,
      first_review: DateTime.now().plus({ months: 2 }),
      final_review: DateTime.now().plus({ months: 3 }),
      application_representative: "",
      deployment_type: "",
      deployment_details: "",
      comments: "",
    });
    setAppNameOptions([]);
    manuallySetDatesRef.current = {
      first_review: false,
      final_review: false,
      moved_to_green_channel_on: false,
    };
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleFormChange = (field, value, isManualDateChange = false) => {
    if (isManualDateChange && (field === "first_review" || field === "final_review" || field === "moved_to_green_channel_on")) {
      manuallySetDatesRef.current[field] = true;
    }

    if (field === "added_to_red_channel" && value) {
      const addedDate = DateTime.isDateTime(value) ? value : DateTime.fromJSDate(value);
      const updates = { added_to_red_channel: addedDate };
      // Always update first_review and final_review when added_to_red_channel changes
      updates.first_review = addedDate.plus({ months: 2 });
      updates.final_review = addedDate.plus({ months: 3 });
      // Reset manual flags since we're auto-updating based on added_to_red_channel
      manuallySetDatesRef.current.first_review = false;
      manuallySetDatesRef.current.final_review = false;
      setFormData((prev) => ({ ...prev, ...updates }));
      return;
    }

    if (field === "status") {
      if (value === "Green") {
        const updates = { status: value };
        if (!manuallySetDatesRef.current.moved_to_green_channel_on) {
          updates.moved_to_green_channel_on = DateTime.now();
        }
        setFormData((prev) => ({ ...prev, ...updates }));
      } else {
        setFormData((prev) => ({ ...prev, status: value, moved_to_green_channel_on: null }));
        manuallySetDatesRef.current.moved_to_green_channel_on = false;
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAppIdInput = (value) => {
    handleFormChange("app_id", value);
    if (!value || value.trim() === "") {
      setFormData((prev) => ({ ...prev, app_id: "", platform: "", portfolio: "", priority: "", incident_dates: "" }));
      setAppNameOptions([]);
    } else {
      setAppNameOptions([]);
    }
    if (appIdTimerRef.current) clearTimeout(appIdTimerRef.current);
    appIdTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppId(value);
        if (data) {
          setFormData((prev) => ({ ...prev, platform: data.platform || "", portfolio: data.portfolio || "" }));
          if (data.app_names && data.app_names.length > 0) {
            setAppNameOptions(data.app_names.map((name) => ({ label: name, value: name })));
          }
        }
      }
    }, 800);
  };

  const handleAppNameInput = (value) => {
    handleFormChange("app_name", value);
    if (appNameTimerRef.current) clearTimeout(appNameTimerRef.current);
    appNameTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppName(value);
        if (data) {
          setFormData((prev) => ({
            ...prev,
            app_id: data.app_id || prev.app_id,
            platform: data.platform || "",
            portfolio: data.portfolio || "",
          }));
        }
      }
    }, 800);
  };

  const handleAppNameSelected = (e) => {
    const selectedName = e.detail?.label || e.detail;
    setFormData((prev) => ({ ...prev, app_name: selectedName }));
  };

  const handleIncidentInput = (value) => {
    handleFormChange("incident", value);
    if (incidentTimerRef.current) clearTimeout(incidentTimerRef.current);
    incidentTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchIncidentDetails(value);
        if (data) {
          setFormData((prev) => ({ ...prev, priority: data.priority || "", incident_dates: data.incident_date || "" }));
        }
      }
    }, 800);
  };

  const handleSubmitApplication = async () => {
    const missingFields = validateMandatoryFields(formData);
    if (missingFields.length > 0) {
      toast.error(`Please fill in the following mandatory fields: ${missingFields.join(", ")}`, {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await submitNewApplication(formData, graphData?.mail);
      toast.success("Application added successfully!", { position: "top-center", autoClose: 3000 });
      setIsAddModalOpen(false);
      await loadData();
    } catch (error) {
      console.error("Error adding application:", error);
      toast.error(error.response?.data?.message || "Failed to add to Red Channel", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // EDIT MODAL HANDLERS
  const handleOpenEditModal = (rowData) => {
    const originalData = rowData._originalData;
    setOriginalAppName(originalData.app_name || "");
    setOriginalIncidentId(originalData.incident || "");
    setEditFormData({
      app_id: originalData.app_id || "",
      app_name: originalData.app_name || "",
      platform: originalData.platform || "",
      portfolio: originalData.portfolio || "",
      status: originalData.status || "",
      trigger_type: originalData.trigger_type || "",
      incident: originalData.incident || "",
      priority: originalData.priority || "",
      incident_dates: originalData.incident_dates || "",
      added_to_red_channel: originalData.added_to_red_channel
        ? DateTime.fromISO(originalData.added_to_red_channel)
        : null,
      moved_to_green_channel_on: originalData.moved_to_green_channel_on
        ? DateTime.fromISO(originalData.moved_to_green_channel_on)
        : null,
      first_review: originalData.first_review ? DateTime.fromISO(originalData.first_review) : null,
      final_review: originalData.final_review ? DateTime.fromISO(originalData.final_review) : null,
      application_representative: originalData.application_representative || "",
      deployment_type: originalData.deployment_type || "",
      deployment_details: originalData.deployment_details || "",
      comments: originalData.comments || "",
    });
    setEditAppNameOptions([]);
    editManuallySetDatesRef.current = {
      first_review: !!originalData.first_review,
      final_review: !!originalData.final_review,
      moved_to_green_channel_on: !!originalData.moved_to_green_channel_on,
    };
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleEditFormChange = (field, value, isManualDateChange = false) => {
    if (isManualDateChange && (field === "first_review" || field === "final_review" || field === "moved_to_green_channel_on")) {
      editManuallySetDatesRef.current[field] = true;
    }

    if (field === "added_to_red_channel" && value) {
      const addedDate = DateTime.isDateTime(value) ? value : DateTime.fromJSDate(value);
      const updates = { added_to_red_channel: addedDate };
      // Always update first_review and final_review when added_to_red_channel changes
      updates.first_review = addedDate.plus({ months: 2 });
      updates.final_review = addedDate.plus({ months: 3 });
      // Reset manual flags since we're auto-updating based on added_to_red_channel
      editManuallySetDatesRef.current.first_review = false;
      editManuallySetDatesRef.current.final_review = false;
      setEditFormData((prev) => ({ ...prev, ...updates }));
      return;
    }

    if (field === "status") {
      if (value === "Green") {
        const updates = { status: value };
        if (!editManuallySetDatesRef.current.moved_to_green_channel_on) {
          updates.moved_to_green_channel_on = DateTime.now();
        }
        setEditFormData((prev) => ({ ...prev, ...updates }));
      } else {
        setEditFormData((prev) => ({ ...prev, status: value, moved_to_green_channel_on: null }));
        editManuallySetDatesRef.current.moved_to_green_channel_on = false;
      }
      return;
    }

    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditAppIdInput = (value) => {
    handleEditFormChange("app_id", value);
    if (!value || value.trim() === "") {
      setEditFormData((prev) => ({ ...prev, app_id: "", platform: "", portfolio: "", priority: "", incident_dates: "" }));
      setEditAppNameOptions([]);
    } else {
      setEditAppNameOptions([]);
    }
    if (editAppIdTimerRef.current) clearTimeout(editAppIdTimerRef.current);
    editAppIdTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppId(value);
        if (data) {
          setEditFormData((prev) => ({ ...prev, platform: data.platform || "", portfolio: data.portfolio || "" }));
          if (data.app_names && data.app_names.length > 0) {
            setEditAppNameOptions(data.app_names.map((name) => ({ label: name, value: name })));
          }
        }
      }
    }, 800);
  };

  const handleEditAppNameInput = (value) => {
    handleEditFormChange("app_name", value);
    if (editAppNameTimerRef.current) clearTimeout(editAppNameTimerRef.current);
    editAppNameTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchAppDetailsByAppName(value);
        if (data) {
          setEditFormData((prev) => ({
            ...prev,
            app_id: data.app_id || prev.app_id,
            platform: data.platform || "",
            portfolio: data.portfolio || "",
          }));
        }
      }
    }, 800);
  };

  const handleEditAppNameSelected = (e) => {
    const selectedName = e.detail?.label || e.detail;
    setEditFormData((prev) => ({ ...prev, app_name: selectedName }));
  };

  const handleEditIncidentInput = (value) => {
    handleEditFormChange("incident", value);
    if (editIncidentTimerRef.current) clearTimeout(editIncidentTimerRef.current);
    editIncidentTimerRef.current = setTimeout(async () => {
      if (value) {
        const data = await fetchIncidentDetails(value);
        if (data) {
          setEditFormData((prev) => ({ ...prev, priority: data.priority || "", incident_dates: data.incident_date || "" }));
        }
      }
    }, 800);
  };

  const handleSubmitEditApplication = async () => {
    const missingFields = validateMandatoryFields(editFormData);
    if (missingFields.length > 0) {
      toast.error(`Please fill in the following mandatory fields: ${missingFields.join(", ")}`, {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    setIsEditSubmitting(true);
    try {
      await updateApplication(editFormData, graphData?.mail, originalAppName, originalIncidentId);
      toast.success("Application updated successfully!", { position: "top-center", autoClose: 3000 });
      setIsEditModalOpen(false);
      await loadData();
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error(error.response?.data?.message || "Failed to update application", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // Helper function to get unique column values
  const getUniqueColumnValues = (data, columnId) => {
    if (!data || data.length === 0) return [];
    
    const values = [];
    let hasBlanks = false;
    
    data.forEach(item => {
      let value = item[columnId];
      if (value === null || value === undefined || value === '' || value === '-' || value === ' ') {
        hasBlanks = true;
      } else {
        values.push(String(value));
      }
    });
    
    const uniqueValues = [...new Set(values)].sort();
    if (hasBlanks) uniqueValues.push('[Blanks]');
    
    return uniqueValues;
  };

  // Helper function to apply column filters
  const applyColumnFilters = (data, filters) => {
    if (Object.keys(filters).length === 0) return data;

    return data.filter(item => {
      return Object.entries(filters).every(([columnId, selectedValues]) => {
        if (!Array.isArray(selectedValues) || selectedValues.length === 0) return true;
        
        let itemValue = item[columnId];
        let matchValue = '';
        
        if (itemValue === null || itemValue === undefined || itemValue === '' || itemValue === '-' || itemValue === ' ') {
          matchValue = '[Blanks]';
        } else {
          matchValue = String(itemValue);
        }
        
        return selectedValues.includes(matchValue);
      });
    });
  };

  // Handle column filter changes for different tables
  const handlePlatformColumnFilterChange = (columnId, selectedValues) => {
    setPlatformColumnFilters(prev => {
      const newFilters = { ...prev };
      const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];
      if (valuesArray.length === 0) {
        delete newFilters[columnId];
      } else {
        newFilters[columnId] = valuesArray;
      }
      return newFilters;
    });
  };

  const handlePortfolioColumnFilterChange = (columnId, selectedValues) => {
    setPortfolioColumnFilters(prev => {
      const newFilters = { ...prev };
      const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];
      if (valuesArray.length === 0) {
        delete newFilters[columnId];
      } else {
        newFilters[columnId] = valuesArray;
      }
      return newFilters;
    });
  };

  const handleChannelColumnFilterChange = (columnId, selectedValues) => {
    setChannelColumnFilters(prev => {
      const newFilters = { ...prev };
      const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];
      if (valuesArray.length === 0) {
        delete newFilters[columnId];
      } else {
        newFilters[columnId] = valuesArray;
      }
      return newFilters;
    });
  };

  // Render column filter function
  const renderColumnFilter = (columnId, label, dataKey, data, filterState, onFilterChange) => {
    const uniqueValues = getUniqueColumnValues(data, dataKey);
    const selectedValues = filterState[dataKey] || [];
    const isFiltered = selectedValues.length > 0;

    return (
      <div 
        slot={`${columnId}_header`} 
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <span>{label}</span>
        <McPopover 
          position="bottom-right" 
          trigger="click"
          click={(e) => e.stopPropagation()}
        >
          <McButton
            appearance="neutral"
            fit="small"
            hiddenlabel={true}
            icon={isFiltered ? "funnel-solid" : "funnel"}
            label="Show filter menu"
            padding="compact"
            slot="trigger"
            variant="plain"
            click={(e) => e.stopPropagation()}
          />
          <div 
            style={{ padding: '16px 12px', maxHeight: '300px', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <McCheckboxGroup
              legend={label}
              orientation="vertical"
              hiddenlegend
              change={(e) => {
                e.stopPropagation();
                const newSelectedValues = e.detail || [];
                onFilterChange(dataKey, newSelectedValues);
              }}
              click={(e) => e.stopPropagation()}
            >
              {uniqueValues.map((value, index) => (
                <McCheckbox
                  key={`${columnId}-${index}`}
                  label={value}
                  value={value}
                  checked={selectedValues.includes(value)}
                  click={(e) => e.stopPropagation()}
                />
              ))}
            </McCheckboxGroup>
          </div>
        </McPopover>
      </div>
    );
  };

  // Data transformations with filters applied
  const channelTableColumns = useMemo(() => getChannelTableColumns(activeChannelTab), [activeChannelTab]);
  
  // Apply channel tab filter first, then column filters
  const tabFilteredChannelData = useMemo(() => filterChannelData(redGreenChannelData, activeChannelTab), [redGreenChannelData, activeChannelTab]);
  const transformedForFiltering = useMemo(() => transformChannelData(tabFilteredChannelData, sortColumn, sortDirection), [tabFilteredChannelData, sortColumn, sortDirection]);
  const filteredChannelData = useMemo(() => applyColumnFilters(transformedForFiltering, channelColumnFilters), [transformedForFiltering, channelColumnFilters]);
  
  // Calculate unique app names count
  const uniqueAppNamesCount = useMemo(() => {
    const uniqueAppNames = new Set(filteredChannelData.map(row => row.app_name));
    return uniqueAppNames.size;
  }, [filteredChannelData]);
  
  const totalPages = Math.ceil(filteredChannelData.length / RECORDS_PER_PAGE);
  const paginatedChannelData = filteredChannelData.slice(
    (currentPage - 1) * RECORDS_PER_PAGE,
    currentPage * RECORDS_PER_PAGE
  );
  const transformedChannelData = useMemo(
    () => paginatedChannelData,
    [paginatedChannelData]
  );

  const handlePageChange = (event) => {
    setCurrentPage(event.detail);
  };

  const handleTableSorted = ({ detail: { column, direction } }) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <ThemeProvider theme={darkPrimaryTheme}>
      <div className="home" style={{ padding: "30px 40px", paddingBottom: "60px" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h1 style={{ fontWeight: "600", fontSize: "1.75rem", color: "#333", margin: 0 }}>
            Red/Green Channel Tracker
          </h1>
          <div style={{display: "flex", gap: "10px", alignItems: "center" }}>
                <McButton
                label="Refresh"
                appearance="neutral"
                variant="outlined"
                fit="medium"
                click={handleRefresh}
                disabled={loading}
                icon="arrow-clockwise"
              />
          {(userRole === "Admin" || userRole === "Change_Manager") && (
            <McButton
              label="Add to Red Channel"
              appearance="primary"
              variant="filled"
              click={handleOpenAddModal}
              icon="plus"
              fit = "medium"
            />
          )}
          </div>
           
        </div>

        {/* Analytics Section */}
        <AnalyticsSection
          redGreenAnalytics={redGreenAnalytics}
          loading={loading}
          onDownloadPlatform={handleDownloadPlatformAnalytics}
          onDownloadPortfolio={handleDownloadPortfolioAnalytics}
          renderPlatformColumnFilter={(columnId, label, dataKey, data) => 
            renderColumnFilter(columnId, label, dataKey, data, platformColumnFilters, handlePlatformColumnFilterChange)
          }
          renderPortfolioColumnFilter={(columnId, label, dataKey, data) => 
            renderColumnFilter(columnId, label, dataKey, data, portfolioColumnFilters, handlePortfolioColumnFilterChange)
          }
          platformColumnFilters={platformColumnFilters}
          portfolioColumnFilters={portfolioColumnFilters}
          applyColumnFilters={applyColumnFilters}
        />

        {/* Channel Tables Section */}
        <div style={{ marginTop: "40px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
            <h2 style={{ fontSize: "1.5rem", fontWeight: "600", color: "#333", margin: 0 }}>
              Details
            </h2>
           
          </div>

          <McTabBar tabchange={handleChannelTabChange} currentindex={getChannelTabIndex()}>
            {CHANNEL_TAB_LABELS.map((label, index) => (
              <React.Fragment key={label}>
                <McTab slot="tab" label={label} active={activeChannelTab === label} />
                <div slot="panel">
                  <div style={{ marginTop: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                      <span style={{ fontSize: "1rem", fontWeight: "600", color: label === "Red Channel" ? "#e75858" : label === "Moved to Green Channel" ? "#00786b" : "#0077b2" }}>
                        Total {label} Apps: {uniqueAppNamesCount}
                      </span>
                       <div style={{ display: "flex", gap: "10px" }}>
             
                      <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={handleDownloadClick}
                        disabled={loading}
                      >
                        <span slot="trailingicon">
                          <mc-icon icon="tray-arrow-down" color="white"></mc-icon>
                        </span>
                      </McButton>
                    </div>
                    </div>
                    <McTable
                      data={transformedChannelData}
                      columns={channelTableColumns}
                      datakey="id"
                      headersticky
                      fit="small"
                      sortmanual
                      sortchange={handleTableSorted}
                      sortdefaultcolumnid={sortColumn}
                      sortdefaultdirection={sortDirection}
                      customstyles=".mds-table { max-height: 60vh !important; }"
                      stateslotheight="200px"
                    >
                      {loading ? (
                        <mc-loading-indicator slot="state"></mc-loading-indicator>
                      ) : (
                        transformedChannelData?.length === 0 && (
                          <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.5rem", fontWeight: "600" }}>
                            No Data Found
                          </div>
                        )
                      )}
                      {/* Column filters for channel tables */}
                      {renderColumnFilter('app_id', 'App ID', 'app_id', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('app_name', 'App Name', 'app_name', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('platform', 'Platform', 'platform', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('portfolio', 'Portfolio', 'portfolio', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('trigger_type', 'Trigger', 'trigger_type', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('incident', 'Incident', 'incident', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('priority', 'Priority', 'priority', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('application_representative', 'App Representative', 'application_representative', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('deployment_type', 'Deployment Type', 'deployment_type', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {renderColumnFilter('deployment_details', 'Deployment Details', 'deployment_details', transformedForFiltering, channelColumnFilters, handleChannelColumnFilterChange)}
                      {transformedChannelData?.map((row) => (
                        <React.Fragment key={row.id}>
                          <div slot={`${row.id}_app_id`}>
                            <span
                              onClick={() => handleAppIdClick(row.app_name, row.incident)}
                              style={{ color: "#0077b2", textDecoration: "underline", cursor: "pointer" }}
                            >
                              {row.app_id}
                            </span>
                          </div>
                          <div slot={`${row.id}_status`}>
                            <mc-tag appearance={row.status === "Red" ? "error" : "success"} fit="small">
                              {row.status}
                            </mc-tag>
                          </div>
                          <div slot={`${row.id}_edit_action`}>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <McButton
                                fit="small"
                                click={() => handleOpenStatisticsModal(row)}
                                icon="eye"
                                hiddenlabel={true}
                                appearance="neutral"
                                variant="outlined"
                              />
                              {(userRole === "Admin" || userRole === "Change_Manager") && (
                                <McButton
                                  fit="small"
                                  click={() => handleOpenEditModal(row)}
                                  icon="pencil"
                                  hiddenlabel={true}
                                  appearance="neutral"
                                  variant="outlined"
                                />
                              )}
                              {(userRole === "Admin" || userRole === "Change_Manager") && (
                                <McButton
                                  fit="small"
                                  click={() => handleOpenDeleteModal(row)}
                                  icon="trash"
                                  hiddenlabel={true}
                                  appearance="neutral"
                                  variant="outlined"
                                />
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </McTable>
                    {!loading && filteredChannelData.length > RECORDS_PER_PAGE && (
                      <div style={{ paddingTop: "1rem", display: "flex", justifyContent: "center" }}>
                        <McPagination currentpage={currentPage} totalpages={totalPages} pagechange={handlePageChange} />
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </McTabBar>
        </div>
      </div>

      {/* Add Application Modal */}
      <McModal
        heading="Add to Red Channel"
        dimension="large"
        padding="default"
        open={isAddModalOpen}
        closed={handleCloseAddModal}
        closing={handleCloseAddModal}
        zindex={999}
      >
        <ApplicationFormFields
          formData={formData}
          appNameOptions={appNameOptions}
          appNameTypeaheadRef={appNameTypeaheadRef}
          onAppIdInput={handleAppIdInput}
          onAppNameInput={handleAppNameInput}
          onAppNameSelected={handleAppNameSelected}
          onIncidentInput={handleIncidentInput}
          onFormChange={handleFormChange}
        />
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isSubmitting}
          click={handleCloseAddModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          disabled={isSubmitting}
          loading={isSubmitting}
          click={handleSubmitApplication}
        >
          Add
        </McButton>
      </McModal>

      {/* Edit Application Modal */}
      <McModal
        heading="Edit Record"
        dimension="large"
        padding="default"
        open={isEditModalOpen}
        closed={handleCloseEditModal}
        closing={handleCloseEditModal}
        zindex={999}
      >
        <ApplicationFormFields
          formData={editFormData}
          appNameOptions={editAppNameOptions}
          appNameTypeaheadRef={editAppNameTypeaheadRef}
          onAppIdInput={handleEditAppIdInput}
          onAppNameInput={handleEditAppNameInput}
          onAppNameSelected={handleEditAppNameSelected}
          onIncidentInput={handleEditIncidentInput}
          onFormChange={handleEditFormChange}
          isEditMode={true}
        />
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isEditSubmitting}
          click={handleCloseEditModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          disabled={isEditSubmitting}
          loading={isEditSubmitting}
          click={handleSubmitEditApplication}
        >
          Save
        </McButton>
      </McModal>

      {/* Statistics Modal */}
      <StatisticsModal
        isOpen={isStatisticsModalOpen}
        loading={statisticsLoading}
        selectedRow={selectedRowForStats}
        statisticsData={statisticsData}
        incidentCounts={incidentCounts}
        postmortemPreventiveMeasures={postmortemPreventiveMeasures}
        onClose={handleCloseStatisticsModal}
      />

      {/* App Details Modal */}
      <AppDetailsModal
        isOpen={isAppDetailsModalOpen}
        loading={appDetailsLoading}
        appDetailsData={appDetailsData}
        onClose={handleCloseAppDetailsModal}
      />

      {/* Delete Confirmation Modal */}
      <McModal
        heading="Delete Record?"
        dimension="small"
        padding="default"
        open={isDeleteModalOpen}
        closed={handleCloseDeleteModal}
        closing={handleCloseDeleteModal}
        zindex={999}
        height={"40%"}
      >
        <div style={{ }}>
          {recordToDelete && (
            <div style={{ marginTop: "1rem", padding: "0.75rem", borderRadius: "4px" }}>
              <p style={{ margin: "0.25rem 0", fontSize: "0.75rem" }}><strong>App Name:</strong> {recordToDelete.app_name}</p>
              <p style={{ margin: "0.25rem 0", fontSize: "0.75rem" }}><strong>Incident:</strong> {recordToDelete.incident}</p>
            </div>
          )}
        </div>
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isDeleting}
          click={handleCloseDeleteModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="error"
          fit="medium"
          disabled={isDeleting}
          loading={isDeleting}
          click={handleConfirmDelete}
        >
          Delete
        </McButton>
      </McModal>
    </ThemeProvider>
  );
}
